import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";

const API = process.env.REACT_APP_BACKEND_URL;

const HEADERS = ["Customer", "Item", "SKU", "Priority", "Min Qty", "Max Qty"];

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

// Six consecutive months starting at the given year and 0-based month.
const buildMonths = (year, monthIndex) => {
  const months = [];

  for (let i = 0; i < 6; i++) {
    const date = new Date(year, monthIndex + i, 1);
    const label = date.toLocaleString("en-IN", { month: "short" });

    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
      label,
      fullLabel: `${label} ${date.getFullYear()}`,
    });
  }

  return months;
};

const getNextSixMonths = () => {
  const today = new Date();

  // Start from the current month so this month's demand can be uploaded.
  return buildMonths(today.getFullYear(), today.getMonth());
};

// The six months of a stored report ("YYYY-MM" start month).
const getReportMonths = (startMonth) => {
  const [year, month] = String(startMonth || "").split("-").map(Number);

  return year && month ? buildMonths(year, month - 1) : getNextSixMonths();
};

// A month heading of row 2: "Oct 2026", "Oct-26", an Excel date, or only "Oct" (older templates).
const parseMonthHeader = (value) => {
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? { year: parsed.y, month: parsed.m } : null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1 };
  }

  const match = String(value || "")
    .trim()
    .toLowerCase()
    .match(/^([a-z]+)[\s\-'/.,]*(\d{2}|\d{4})?$/);

  if (!match) return null;

  const month = MONTH_NAMES.indexOf(match[1].slice(0, 3)) + 1;

  if (!month) return null;

  if (!match[2]) return { year: null, month };

  let year = Number(match[2]);
  if (year < 100) year += 2000;

  return { year, month };
};

// Months of the uploaded file, read from its headings, so the quantities land in the months the
// file was filled for (not the months of the upload day). A past month is not accepted.
const getFileMonths = (headerRow) => {
  const current = getNextSixMonths();
  const parsed = [0, 1, 2, 3, 4, 5].map((index) =>
    parseMonthHeader(headerRow?.[6 + index]),
  );

  if (parsed.some((item) => !item)) {
    throw new Error(
      "The month headings in row 2 (columns G to L) are missing or invalid. Download the latest template.",
    );
  }

  // Older templates have only the month name, so they are accepted only for the current six months.
  if (parsed.every((item) => item.year === null)) {
    const matchesCurrent = parsed.every(
      (item, index) => item.month === Number(current[index].value.slice(5)),
    );

    if (!matchesCurrent) {
      throw new Error(
        `The month headings do not match the current planning months (${current[0].fullLabel} to ${current[5].fullLabel}). Past months cannot be uploaded. Download the latest template.`,
      );
    }

    return current;
  }

  if (parsed.some((item) => item.year === null)) {
    throw new Error(
      "Every month heading in row 2 must include the year (for example Oct 2026). Download the latest template.",
    );
  }

  const months = buildMonths(parsed[0].year, parsed[0].month - 1);

  const consecutive = parsed.every(
    (item, index) =>
      item.year === Number(months[index].value.slice(0, 4)) &&
      item.month === Number(months[index].value.slice(5)),
  );

  if (!consecutive) {
    throw new Error("The month headings must be six consecutive months.");
  }

  if (months[0].value < current[0].value) {
    throw new Error(
      `This sales report starts in ${months[0].fullLabel}, which is a past month. Upload a report starting from ${current[0].fullLabel} or later.`,
    );
  }

  return months;
};

const authHeader = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  return {
    headers: {
      Authorization: `Bearer ${storedUser?.accessToken}`,
    },
  };
};

const getNumber = (value, row, column) => {
  if (value === "" || value === null || value === undefined) return 0;

  const number = Number(value);

  if (Number.isNaN(number)) {
    throw new Error(`Row ${row}: ${column} must be a number.`);
  }

  return number;
};

const SalesReport = () => {
  const fileInputRef = useRef(null);
  const months = getNextSixMonths();

  const [reports, setReports] = useState([]);
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [viewReport, setViewReport] = useState(null);
  const [viewItems, setViewItems] = useState([]);
  // Months read from the uploaded file's headings
  const [fileMonths, setFileMonths] = useState(null);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/sales-report`, authHeader());
      setReports(res.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.Exception || "Unable to load sales reports.",
      );
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const clearPreview = () => {
    setFile(null);
    setPreviewRows([]);
    setFileMonths(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const firstRow = [...HEADERS, "Requirement", "", "", "", "", ""];
    const secondRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      // The year is part of the heading so the upload knows exactly which months the file is for
      ...months.map((item) => item.fullLabel),
    ];

    const sheet = XLSX.utils.aoa_to_sheet([firstRow, secondRow]);
    sheet["!merges"] = [{ s: { r: 0, c: 6 }, e: { r: 0, c: 11 } }];
    sheet["!cols"] = [
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Sales Report");
    XLSX.writeFile(workbook, "Sales_Report_Template.xlsx");
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setError("");
    setSuccess("");
    setLoading(true);
    setFile(null);
    setPreviewRows([]);
    setFileMonths(null);

    try {
      const workbook = XLSX.read(await selectedFile.arrayBuffer(), {
        type: "array",
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        blankrows: false,
      });

      if (rows.length < 3) throw new Error("The Excel file has no data.");

      HEADERS.forEach((header, index) => {
        if (String(rows[0][index] || "").trim() !== header) {
          throw new Error(`Invalid template. Expected column: ${header}`);
        }
      });

      if (String(rows[0][6] || "").trim() !== "Requirement") {
        throw new Error("Requirement header is missing.");
      }

      // The quantities belong to the months in the file's headings, not to the months of today
      const uploadMonths = getFileMonths(rows[1]);

      const dataRows = rows
        .slice(2)
        .filter((row) =>
          row.some((value) => String(value || "").trim() !== ""),
        );

      if (!dataRows.length) throw new Error("The Excel file has no data.");

      const items = dataRows.map((row, index) => {
        const rowNumber = index + 3;
        const itemCode = String(row[1] || "").trim();

        if (!itemCode) throw new Error(`Row ${rowNumber}: Item is required.`);

        return {
          id: index + 1,
          customer: String(row[0] || "").trim(),
          itemCode,
          sku: String(row[2] || "").trim(),
          // A blank priority is sent as empty so it is planned last, not as 0 (the highest)
          priority:
            String(row[3] ?? "").trim() === ""
              ? null
              : getNumber(row[3], rowNumber, "Priority"),
          minQty: getNumber(row[4], rowNumber, "Min Qty"),
          maxQty: getNumber(row[5], rowNumber, "Max Qty"),
          monthQty: uploadMonths.map((month, monthIndex) =>
            getNumber(row[6 + monthIndex], rowNumber, month.fullLabel),
          ),
        };
      });

      setFile(selectedFile);
      setFileMonths(uploadMonths);
      setPreviewRows(items);
    } catch (err) {
      setError(
        err?.response?.data?.Exception ||
          err.message ||
          "Unable to read Excel file.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file || previewRows.length === 0 || !fileMonths) return;

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      await axios.post(
        `${API}/sales-report`,
        {
          fileName: file.name,
          months: fileMonths.map((month) => month.value),
          items: previewRows.map(({ id, ...item }) => item),
        },
        authHeader(),
      );

      setSuccess(`${file.name} uploaded successfully.`);
      clearPreview();
      fetchReports();
    } catch (err) {
      setError(
        err?.response?.data?.Exception || err.message || "Upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (report) => {
    try {
      const res = await axios.get(
        `${API}/sales-report/${report.reportId}/items`,
        authHeader(),
      );
      setViewItems(res.data || []);
      setViewReport(report);
    } catch (err) {
      setError(
        err?.response?.data?.Exception || "Unable to load report items.",
      );
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete ${report.fileName}?`)) return;

    try {
      await axios.delete(
        `${API}/sales-report/${report.reportId}`,
        authHeader(),
      );
      setSuccess("Sales report deleted successfully.");
      fetchReports();
    } catch (err) {
      setError(err?.response?.data?.Exception || "Unable to delete report.");
    }
  };

  const previewColumns = useMemo(
    () => [
      { field: "customer", headerName: "Customer", minWidth: 160, flex: 1 },
      { field: "itemCode", headerName: "Item", minWidth: 150, flex: 1 },
      { field: "sku", headerName: "SKU", minWidth: 130, flex: 1 },
      { field: "priority", headerName: "Priority", width: 90 },
      { field: "minQty", headerName: "Min Qty", width: 100 },
      { field: "maxQty", headerName: "Max Qty", width: 100 },
      ...(fileMonths || months).map((month, index) => ({
        field: `month${index + 1}`,
        headerName: month.fullLabel,
        width: 110,
        valueGetter: (_, row) => row?.monthQty?.[index] ?? 0,
      })),
    ],
    [months, fileMonths],
  );

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Sales Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload six months of customer demand, starting this month, for
          production planning.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, whiteSpace: "pre-line" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid #e3e7ef", borderRadius: 2, mb: 3 }}
      >
        <Stack>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              Sales Report Excel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the downloaded template and upload customer requirements for
              six months, starting this month.
            </Typography>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Button
              className="btn-outlined"
              startIcon={<DownloadOutlined />}
              onClick={downloadTemplate}
              disabled={loading || uploading}
            >
              Download Template
            </Button>
            <Button
              className="btn-outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading}
            >
              {loading ? "Processing..." : "Choose Excel"}
            </Button>
            {previewRows.length > 0 && (
              <Button
                className="btn-outlined btn-reject"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={clearPreview}
                disabled={uploading}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {previewRows.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
        >
          <Chip label={`Rows: ${previewRows.length}`} variant="outlined" />
          <Chip label="Ready to submit" color="success" variant="outlined" />
        </Stack>
      )}

      {previewRows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e3e7ef",
            borderRadius: 2,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e3e7ef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                Sales Report Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review the imported rows before submitting the sales report.
              </Typography>
            </Box>
            <Button
              className="btn-contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={uploading || loading || previewRows.length === 0}
            >
              {uploading ? "Adding Sales Report..." : "Submit Sales Report"}
            </Button>
          </Box>

          <DataGrid
            rows={previewRows}
            columns={previewColumns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f7f9fc",
                fontWeight: 700,
              },
              "& .MuiDataGrid-cell": { fontSize: "0.875rem" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#fafbfc" },
            }}
          />
        </Paper>
      )}

      {reports.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e3e7ef",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1.5fr 1fr 1fr",
              p: 1.5,
              backgroundColor: "#f7f9fc",
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            <span>File</span>
            <span>Uploaded on</span>
            <span>Starts from</span>
            <span />
          </Box>

          {reports.map((report, index) => (
            <Box
              key={report.reportId}
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1.5fr 1fr 1fr",
                p: 1.5,
                borderTop: "1px solid #eef1f6",
                alignItems: "center",
                fontSize: "0.875rem",
              }}
            >
              <span>
                {report.fileName}{" "}
                {index === 0 && (
                  <Chip size="small" color="primary" label="Latest" />
                )}
              </span>
              <span>{new Date(report.uploadDate).toLocaleString("en-IN")}</span>
              <span>{report.startMonth}</span>
              <span>
                <IconButton size="small" onClick={() => handleView(report)}>
                  <VisibilityOutlined fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(report)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Box>
          ))}
        </Paper>
      )}

      {reports.length === 0 && previewRows.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            minHeight: 300,
            border: "1px dashed #cbd2df",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 4,
          }}
        >
          <Box>
            <UploadFileOutlined
              sx={{ fontSize: 52, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload your sales report
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 550, mx: "auto", mt: 0.5 }}
            >
              Download the template, enter customer demand for the next six
              months, then upload it here.
            </Typography>
          </Box>
        </Paper>
      )}

      <Dialog
        open={!!viewReport}
        onClose={() => setViewReport(null)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>{viewReport?.fileName}</DialogTitle>
        <DialogContent sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 1150 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "1.2fr 1.2fr 1fr 0.6fr 0.8fr 0.8fr repeat(6, 0.7fr)",
                p: 1,
                backgroundColor: "#f7f9fc",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              <span>Customer</span>
              <span>Item</span>
              <span>SKU</span>
              <span>Prio</span>
              <span>Min</span>
              <span>Max</span>
              {getReportMonths(viewReport?.startMonth).map((month) => (
                <span key={month.value}>{month.fullLabel}</span>
              ))}
            </Box>

            {viewItems.map((item) => (
              <Box
                key={item.salesItemId}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "1.2fr 1.2fr 1fr 0.6fr 0.8fr 0.8fr repeat(6, 0.7fr)",
                  p: 1,
                  borderTop: "1px solid #eef1f6",
                  fontSize: "0.85rem",
                }}
              >
                <span>{item.customer}</span>
                <span>{item.itemCode}</span>
                <span>{item.sku}</span>
                <span>{item.priority}</span>
                <span>{item.minQty}</span>
                <span>{item.maxQty}</span>
                <span>{item.month1Qty}</span>
                <span>{item.month2Qty}</span>
                <span>{item.month3Qty}</span>
                <span>{item.month4Qty}</span>
                <span>{item.month5Qty}</span>
                <span>{item.month6Qty}</span>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReport(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesReport;
